import React, { useRef, useState } from 'react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import SvgTruck from '@icons/truck.svg?react';
import SvgPlus from '@icons/plus.svg?react';
import SvgEdit from '@icons/pencil-inverted.svg?react';
import SvgDelete from '@icons/delete-trash.svg?react';
import SvgClose from '@icons/close.svg?react';
import SvgChevron from '@icons/chevron-thin.svg?react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { generateUUID } from '@utils/utils.tsx';
import { formatPriceRequest } from '@utils/priceUtils.ts';
import { applicableRow, missingMuPrices, reconcileMatrix, resolveContractedPrices } from '@utils/contractedPriceTable.ts';
import { ContractedCarrierForm, PriceIntervalForm, PriceTableForm } from '@components/ringil3/Features/Contracted/types.ts';
import ContractedPricesSidebar from '@components/ringil3/Features/Contracted/ContractedPricesSidebar.tsx';
import ContractedCarrierFields, { useContractedCarrierCandidates } from '@components/ringil3/Features/Contracted/ContractedCarrierFields.tsx';
import PriceTableStructureSidebar from '@components/ringil3/Features/Contracted/PriceTableStructureSidebar.tsx';
import PriceLevelPlanSidebar from '@components/ringil3/Features/Contracted/PriceLevelPlanSidebar.tsx';
import PriceLevelTimeline from '@components/ringil3/Features/Contracted/PriceLevelTimeline.tsx';

export interface ContractedPricing {
  table: PriceTableForm;
  carriers: ContractedCarrierForm[];
}

interface ComponentProps {
  table: PriceTableForm;
  carriers: ContractedCarrierForm[];
  onChange: (value: ContractedPricing) => void;
  /**
   * line: every carrier as an inline block with its whole matrix (the Linka editor).
   * expedition: carrier chips and one price per carrier at the level of the deadline day; a carrier is edited in a sidebar.
   */
  mode: 'line' | 'expedition';
  /** Name of the Linka, shown in the sidebars. */
  subjectName?: string;
  readOnly?: boolean;
  /** Shown in the sidebars, e.g. that edits apply to this shipment only. */
  infoText?: string;
  /** expedition: ISO day of the response deadline - picks the level. */
  deadlineDate?: string;
  /** expedition: the shipment's laden factor, when it has one. */
  shipmentLadenFactor?: number | null;
  /** expedition detail: the level the stored prices were taken from (the deadline may have moved since). */
  pricedLevelName?: string;
  /** expedition: MU codes of the shipment's orders, priced per MU; without them every MU of the table is shown. */
  shipmentMuCodes?: string[];
  errorMessage?: string;
}


const emptyCarrier = (): ContractedCarrierForm => ({ carrierId: null, carrierName: null, users: [], prices: {} });

type CarrierSidebar = { visible: false } | { visible: true; carrierId?: string };

/**
 * The contracted carriers of a Linka or of a CONTRACTED shipment: the price level plan, the carriers and their price
 * matrices, the structure / plan sidebars. The structure and the plan are shared by all carriers.
 */
const ContractedCarriersSection: React.FC<ComponentProps> = ({
  table,
  carriers,
  onChange,
  mode,
  subjectName,
  readOnly = false,
  infoText,
  deadlineDate,
  shipmentLadenFactor,
  shipmentMuCodes,
  pricedLevelName,
  errorMessage,
}) => {
  const { tr } = useTranslationLgs();
  const [structureVisible, setStructureVisible] = useState(false);
  const [plan, setPlan] = useState<{ visible: boolean; addPeriod: boolean }>({ visible: false, addPeriod: false });
  const [carrierSidebar, setCarrierSidebar] = useState<CarrierSidebar>({ visible: false });
  const [pricesShown, setPricesShown] = useState(true);
  const candidates = useContractedCarrierCandidates(mode === 'line' && !readOnly);

  const list = carriers ?? [];
  // line mode: a client key per carrier block, stable while a carrier is picked and when a block above is removed
  const blockKeys = useRef<string[]>([]);
  while (blockKeys.current.length < list.length) blockKeys.current.push(generateUUID());
  blockKeys.current.length = list.length;
  const expedition = mode === 'expedition';
  const pricedLevel = pricedLevelName ? table.levels.find(l => l.name === pricedLevelName) : undefined;
  // a frozen level is priced on one of its own days, so the same resolution applies
  const pricedTable = pricedLevel ? { ...table, intervals: [{ key: 'priced', levelKey: pricedLevel.key, validFrom: '0000-01-01', validTo: '9999-12-31' }] } : table;
  const muCodes = shipmentMuCodes?.length ? shipmentMuCodes : Array.from(new Set(table.rows.map(r => r.cargoTemplateCode).filter(Boolean)));
  const resolved = expedition ? resolveContractedPrices(pricedTable, list, pricedLevel ? '2000-01-01' : deadlineDate, shipmentLadenFactor, muCodes) : null;
  const highlight = resolved?.level
    ? { levelKey: resolved.level.key, rowKeys: muCodes.map(mu => applicableRow(table, mu, shipmentLadenFactor)?.key).filter(Boolean) }
    : null;
  const muName = (mu: string) => table.rows.find(r => r.cargoTemplateCode === mu)?.cargoTemplateName || mu;

  const saveStructure = (next: PriceTableForm) => onChange({ table: next, carriers: list.map(c => ({ ...c, prices: reconcileMatrix(next, c.prices) })) });
  const savePlan = (intervals: PriceIntervalForm[]) => onChange({ table: { ...table, intervals }, carriers: list });
  const setCarriers = (next: ContractedCarrierForm[]) => onChange({ table, carriers: next });
  const updateCarrierAt = (index: number, carrier: ContractedCarrierForm) => setCarriers(list.map((c, i) => (i === index ? carrier : c)));

  const sidebars = (
    <>
      <PriceTableStructureSidebar visible={structureVisible} onHide={() => setStructureVisible(false)} table={table} subjectName={subjectName} infoText={infoText} onSave={saveStructure} />
      <PriceLevelPlanSidebar
        visible={plan.visible}
        onHide={() => setPlan({ visible: false, addPeriod: false })}
        table={table}
        subjectName={subjectName}
        infoText={infoText}
        startWithNewPeriod={plan.addPeriod}
        onSave={savePlan}
      />
      {expedition && (
        <ContractedPricesSidebar
          visible={carrierSidebar.visible}
          onHide={() => setCarrierSidebar({ visible: false })}
          table={table}
          initial={carrierSidebar.visible && carrierSidebar.carrierId ? list.find(c => c.carrierId === carrierSidebar.carrierId) : undefined}
          excludedCarrierIds={list.map(c => c.carrierId)}
          infoText={infoText}
          highlight={highlight}
          onSetTable={() => setStructureVisible(true)}
          onSave={saved => {
            const editedId = carrierSidebar.visible ? carrierSidebar.carrierId : undefined;
            setCarriers(editedId ? list.map(c => (c.carrierId === editedId ? saved : c)) : [...list, saved]);
          }}
        />
      )}
    </>
  );

  const timeline = (
    <div className={'flex flex-col gap-3 rounded bg-[#f4f2ef] p-4'}>
      <PriceLevelTimeline
        table={table}
        readOnly={readOnly}
        pricingDate={deadlineDate}
        exportName={subjectName}
        onEditPlan={addPeriod => setPlan({ visible: true, addPeriod })}
      />
      {expedition && !pricedLevel && table.intervals.length > 0 && !resolved?.level && deadlineDate && (
        <COInlineMessage variant={'alert'}>
          {tr('ContractedCarriers.noLevelForDeadline', 'Na den uzávěrky poptávky {{date}} není naplánovaná žádná cenová úroveň. Naplánujte ji.', {
            date: dayjs(deadlineDate).format('D. M. YYYY'),
          })}
        </COInlineMessage>
      )}
      {expedition && pricedLevel && (
        <div className={'text-sm text-content-secondary'} data-testid={'contracted-active-level'}>
          {tr('ContractedCarriers.pricedByFrozenLevel', 'Ceny podle úrovně {{level}}', { level: pricedLevel.name })}
        </div>
      )}
      {expedition && !pricedLevel && resolved?.level && (
        <div className={'text-sm text-content-secondary'} data-testid={'contracted-active-level'}>
          {tr('ContractedCarriers.pricedByLevel', 'Ceny podle úrovně {{level}} (uzávěrka {{date}}), ladenfaktor {{lf}}', {
            level: resolved.level.name,
            date: dayjs(deadlineDate).format('D. M. YYYY'),
            lf: shipmentLadenFactor ?? tr('PriceTable.defaultLadenFactor', 'Výchozí'),
          })}
        </div>
      )}
    </div>
  );

  if (!expedition) {
    return (
      <div className={'flex flex-col gap-4'}>
        {timeline}
        <div className={'font-medium text-base'}>{tr('ContractedCarriers.title', 'Nasmlouvaní dopravci')}</div>
        {list.map((carrier, index) => (
          <div key={blockKeys.current[index]} className={'flex flex-col gap-3 rounded bg-[#f4f2ef] p-4'} data-testid={`contracted-carrier-block-${index}`}>
            <div className={'flex flex-row items-center justify-between'}>
              <div className={'font-medium text-lg'}>{tr('ContractedCarriers.contractedCarrier', 'Nasmlouvaný dopravce')}</div>
              {!readOnly && (
                <RingilButton
                  mode={'table'}
                  icon={<SvgDelete />}
                  tooltip={tr('ContractedCarriers.removeCarrier', 'Odebrat dopravce')}
                  onClick={() => {
                    blockKeys.current.splice(index, 1);
                    setCarriers(list.filter((_, i) => i !== index));
                  }}
                />
              )}
            </div>
            <ContractedCarrierFields
              value={carrier}
              onChange={c => updateCarrierAt(index, c)}
              table={table}
              candidates={candidates}
              excludedCarrierIds={list.map(c => c.carrierId).filter(id => !!id)}
              onSetTable={() => setStructureVisible(true)}
              readOnly={readOnly}
              dataTestIdPrefix={`contracted-carrier-${index}`}
            />
          </div>
        ))}
        {!readOnly && (
          <div>
            <RingilButton
              mode={'text-only'}
              icon={<SvgPlus className={'w-3 h-3'} />}
              label={tr('ContractedCarriers.addCarrier', 'Přidat dopravce')}
              onClick={() => setCarriers([...list, emptyCarrier()])}
              dataTestId={'contracted-add-carrier'}
            />
          </div>
        )}
        {errorMessage && <div className={'text-sm text-complementary-error'}>{errorMessage}</div>}
        {sidebars}
      </div>
    );
  }

  return (
    <div className={'flex flex-col gap-4'}>
      {timeline}
      <div className={'flex flex-col gap-3'}>
        <div className={'flex flex-row items-center justify-between'}>
          <div className={'font-medium text-base'}>{tr('ContractedCarriers.title', 'Nasmlouvaní dopravci')}</div>
          {!readOnly && <RingilButton mode={'text-only'} label={tr('ContractedCarriers.setTable', 'Nastavit tabulku')} onClick={() => setStructureVisible(true)} dataTestId={'contracted-set-table'} />}
        </div>
        <div className={'flex flex-row flex-wrap items-center gap-2'}>
          {list.map(carrier => {
            const missing = resolved?.level && missingMuPrices(resolved, carrier.carrierId).length > 0;
            return (
              <div
                key={carrier.carrierId}
                className={clsx(
                  'flex flex-row items-center gap-2 rounded border border-solid px-3 py-2 text-sm bg-white',
                  missing ? 'border-complementary-error' : 'border-content-quaternary',
                )}
                data-testid={`contracted-carrier-chip-${carrier.carrierId}`}
              >
                <SvgTruck className={clsx('w-4 h-4', missing ? 'text-complementary-error' : 'text-content-secondary')} />
                <span>{carrier.carrierName}</span>
                {!readOnly && (
                  <SvgClose
                    className={'w-3 h-3 text-content-brand cursor-pointer'}
                    onClick={() => setCarriers(list.filter(c => c.carrierId !== carrier.carrierId))}
                    data-testid={`contracted-carrier-remove-${carrier.carrierId}`}
                  />
                )}
              </div>
            );
          })}
          {list.length > 0 && (
            <RingilButton
              mode={'text-only'}
              icon={<SvgChevron className={clsx('w-3 h-3 transition-transform', pricesShown && 'rotate-180')} />}
              label={pricesShown ? tr('ContractedCarriers.hidePrices', 'Skrýt ceny') : tr('ContractedCarriers.showPrices', 'Zobrazit ceny')}
              onClick={() => setPricesShown(!pricesShown)}
              dataTestId={'contracted-toggle-prices'}
            />
          )}
        </div>

        {list.length === 0 && (
          <div className={'text-sm text-content-secondary'}>
            {tr('ContractedCarriers.emptyShipment', 'Přeprava zatím nemá smluvní dopravce. Přidejte dopravce, kontaktní osoby a ceny.')}
          </div>
        )}

        {pricesShown && list.length > 0 && (
          <table className={'w-full text-sm border-collapse'} data-testid={'contracted-price-table'}>
            <thead>
              <tr className={'bg-[#e9ecef] text-left'}>
                <th className={'px-6 py-3 font-medium'}>{tr('ContractedCarriers.colCarrier', 'Dopravce')}</th>
                <th className={'px-6 py-3 font-medium text-right'}>{tr('ContractedCarriers.pricePerUnit', 'Cena / jednotka')}</th>
                <th className={'w-24'} />
              </tr>
            </thead>
            <tbody>
              {list.map(carrier => {
                const prices = resolved?.byCarrier[carrier.carrierId] ?? {};
                return (
                  <tr key={carrier.carrierId} className={'border-b border-solid border-content-quaternary bg-white'}>
                    <td className={'px-6 py-3'}>
                      <div className={'flex flex-col'}>
                        <span>{carrier.carrierName}</span>
                        {carrier.users.length > 0 && <span className={'text-xs text-content-secondary'}>{carrier.users.map(u => u.fullName).join(', ')}</span>}
                      </div>
                    </td>
                    <td className={'px-6 py-3 text-right'} data-testid={`contracted-price-${carrier.carrierId}`}>
                      {muCodes.map(mu => {
                        const price = prices[mu]?.price;
                        return (
                          <div key={mu} className={'whitespace-nowrap'}>
                            {price !== null && price !== undefined ? (
                              `${formatPriceRequest({ price, currency: table.currency })} / ${muName(mu)}`
                            ) : (
                              <span className={'text-complementary-error'}>
                                {resolved?.level
                                  ? tr('ContractedCarriers.missingMuPrice', 'Chybí cena ({{mu}})', { mu: muName(mu) })
                                  : tr('ContractedCarriers.noLevel', 'Bez úrovně')}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </td>
                    <td className={'px-2 py-1 text-right'}>
                      {!readOnly && (
                        <RingilButton
                          mode={'table'}
                          icon={<SvgEdit className={'w-4 h-4'} />}
                          tooltip={tr('ContractedCarriers.editCarrier', 'Upravit smluvní ceny')}
                          onClick={() => setCarrierSidebar({ visible: true, carrierId: carrier.carrierId })}
                          dataTestId={`contracted-edit-${carrier.carrierId}`}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {!readOnly && (
          <div>
            <RingilButton
              mode={'text-only'}
              icon={<SvgPlus className={'w-3 h-3'} />}
              label={tr('ContractedCarriers.addCarrier', 'Přidat dopravce')}
              onClick={() => setCarrierSidebar({ visible: true })}
              dataTestId={'contracted-add-carrier'}
            />
          </div>
        )}
        {errorMessage && <div className={'text-sm text-complementary-error'}>{errorMessage}</div>}
      </div>
      {sidebars}
    </div>
  );
};

export default ContractedCarriersSection;
